import React from "react";
import { MegaphoneIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../contexts/LanguageContext";
import { useStore } from "../contexts/StoreContext";
import DeleteAlert from "../components/DeleteAlert.jsx";
import MessageTable from "./marketing/components/MessageTable.jsx";
import MessageModal from "./marketing/components/MessageModal.jsx";
import { useMarketingData } from "../hooks/useMarketingData";
import Header from "../components/Header";
import Button from "../components/Button";

/**
 * Marketing page component for managing marketing messages
 * This component has been refactored to use:
 * - Custom hook (useMarketingData) for data fetching and state management
 * - Extracted components for better code organization and reusability
 * - React.memo for performance optimization
 */
const Marketing = () => {
  const { t } = useLanguage();
  const {
    // Data
    messages,
    passes,
    formData,
    setFormData,
    isModalOpen,
    isDeleteAlertOpen,
    editingMessage,
    error,
    attemptedType,

    // Loading states
    isLoadingMessages,
    isLoadingPhoneNumbers,
    isLoadingEmailAddresses,

    // Integration data
    phoneNumbers,
    emailAddresses,

    // Handlers
    handleOpenModal,
    handleCloseModal,
    handleSubmit,
    handleDelete,
    handleConfirmDelete,
    setIsDeleteAlertOpen,
    handleMessageTypeChange,
  } = useMarketingData();
  const { canManageStore } = useStore();

  if (isLoadingMessages) {
    return <div className="p-4">{t("common.loading")}</div>;
  }

  return (
    <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
      <Header
        icon={MegaphoneIcon}
        title={t("marketing.title")}
        description={t("marketing.description")}
        rightSection={
          canManageStore && (
            <Button onClick={() => handleOpenModal()} icon={PlusIcon}>
              {t("marketing.new.title")}
            </Button>
          )
        }
      />

      {/* Messages table section */}
      <div className="overflow-x-auto">
        <MessageTable
          messages={messages}
          t={t}
          onEdit={canManageStore ? handleOpenModal : undefined}
          onDelete={canManageStore ? handleDelete : undefined}
        />
      </div>

      {/* Message modal */}
      <MessageModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        isEditing={!!editingMessage}
        passes={passes}
        phoneNumbers={phoneNumbers}
        emailAddresses={emailAddresses}
        isLoadingPhoneNumbers={isLoadingPhoneNumbers}
        isLoadingEmailAddresses={isLoadingEmailAddresses}
        t={t}
        handleMessageTypeChange={handleMessageTypeChange}
        error={error}
        attemptedType={attemptedType}
      />

      {/* Delete confirmation dialog */}
      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => setIsDeleteAlertOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default React.memo(Marketing);
